/* eslint-disable */
// @ts-nocheck

/* ============================================================================
 * Demo data for the Canopus Command "eXeX Demo Facility".
 *
 * Powers a fully self-contained demo when the page is viewed for the
 * dedicated demo facility (id `exex-demo`, see data/demo-facility.ts). There
 * is no real backend behind that facility, so every value here is fabricated
 * but realistic. Surgeon and staff identities come from the baked-in,
 * anonymized `DEMO_SURGEONS` / `DEMO_STAFF` pools below — never from a real
 * facility fetch.
 *
 * Consumers (all gated on `demoMode`, which is true only for the demo
 * facility):
 *   - canopus-command.tsx (feeds the baked pools into the getDemo* helpers)
 *   - tabs/staff.tsx, tabs/surgical-inquiries.tsx, tabs/historical.tsx,
 *     tabs/trends.tsx, tabs/performance.tsx (demo branch in each fetch effect)
 *
 * Periods (today/30d/90d/ytd/all) for the SI aggregates return the same
 * shape; non-`today` periods scale the totals by a fixed multiplier (~22,
 * ~65, ~150, ~400) — the rest of the structure (per-OR, heatmap, monthly
 * trend, etc.) is held constant for simplicity. This is enough for a demo
 * walkthrough; nobody clicks every period.
 * ============================================================================
 */

import type {
  CanopusActiveCase,
  CanopusHistoricalCase,
  CanopusLeaderboardEntry,
  CanopusOrStatus,
  CanopusPerformanceLeaderboard,
  CanopusPhaseTiming,
  CanopusRoomStatus,
  CanopusSIAggregates,
  CanopusSIPeriod,
  CanopusStaffMember,
  CanopusStaffStatus,
  CanopusStep,
  CanopusTrendsAdoptionBucket,
  CanopusTrendsMonthlyEntry,
  CanopusTrendsResult,
  CanopusTrendsRoomGap,
  CanopusTrendsSpecialtyVariance,
  CanopusTrendsSurgeonDurationIntel,
  CanopusTrendsSurgeonProcedureVariance,
  CanopusTrendsVarianceHeatmap,
} from '../../../services/canopusCommand.js';
import type { InsightsSurgeon } from '../../../services/insights.js';
import { DEFAULT_DEMO_FACILITY_NAME } from './demo-facility.js';

// ─── Baked-in anonymized demo pools ───────────────────────────────
// The demo facility has no real backend, so surgeon + staff identities are
// baked in here (anonymized). These replace the real-fetch dependency the
// getDemo* helpers used to have. Emails use DEMO_STAFF_EMAIL_DOMAIN so the
// same curated-pool filters that gate the demo staff list accept them.

export const DEMO_STAFF_EMAIL_DOMAIN = '@exex.demo';

// ~10 surgeons spanning the specialties the demo slots + trends expect:
// Spine (≥3 for OR 3 / OR 7 / OR 5), Neurosurgery (OR 12), Orthopedics
// (OR 1), plus Cardiothoracic Surgery (used by getDemoTrends). Ids are
// stable + sortable — assignSurgeons and the bySurgeon leaderboard sort by id.
export const DEMO_SURGEONS: InsightsSurgeon[] = [
  {
    id: 'demo-surg-01',
    name: 'Dr. Alan Whitfield',
    specialty: 'Spine',
    profiles: 6,
    procedures: 48,
  },
  { id: 'demo-surg-02', name: 'Dr. Priya Raman', specialty: 'Spine', profiles: 5, procedures: 41 },
  { id: 'demo-surg-03', name: 'Dr. Marcus Bell', specialty: 'Spine', profiles: 4, procedures: 33 },
  {
    id: 'demo-surg-04',
    name: 'Dr. Elena Sorensen',
    specialty: 'Neurosurgery',
    profiles: 5,
    procedures: 29,
  },
  {
    id: 'demo-surg-05',
    name: 'Dr. Julian Ford',
    specialty: 'Neurosurgery',
    profiles: 3,
    procedures: 22,
  },
  {
    id: 'demo-surg-06',
    name: 'Dr. Naomi Cortez',
    specialty: 'Orthopedics',
    profiles: 4,
    procedures: 37,
  },
  {
    id: 'demo-surg-07',
    name: 'Dr. Theo Lindqvist',
    specialty: 'Orthopedics',
    profiles: 3,
    procedures: 26,
  },
  {
    id: 'demo-surg-08',
    name: 'Dr. Hannah Weiss',
    specialty: 'Cardiothoracic Surgery',
    profiles: 4,
    procedures: 31,
  },
  {
    id: 'demo-surg-09',
    name: 'Dr. Omar Haddad',
    specialty: 'Cardiothoracic Surgery',
    profiles: 2,
    procedures: 18,
  },
  {
    id: 'demo-surg-10',
    name: 'Dr. Grace Aldridge',
    specialty: 'Spine',
    profiles: 3,
    procedures: 24,
  },
];

// ~14 clinical staff (multi-word names, demo-domain emails, real positions).
// Status fields (onDuty / currentRoom / si*) are placeholders — the demo
// overlays recompute them. Sorted-by-userId order is deterministic.
export const DEMO_STAFF: CanopusStaffStatus = {
  generatedAtIso: new Date(0).toISOString(),
  members: [
    {
      userId: 'demo-staff-01',
      name: 'Jordan Alvarez',
      email: `jalvarez${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Scrub Tech',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-02',
      name: 'Riley Chen',
      email: `rchen${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Circ Nurse',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-03',
      name: 'Morgan Patel',
      email: `mpatel${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Scrub Tech',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-04',
      name: 'Casey Nguyen',
      email: `cnguyen${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'RN',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-05',
      name: 'Taylor Brooks',
      email: `tbrooks${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Circ Nurse',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-06',
      name: 'Devon Reyes',
      email: `dreyes${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Scrub Tech',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-07',
      name: 'Sam Okonkwo',
      email: `sokonkwo${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'RN',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-08',
      name: 'Avery Schmidt',
      email: `aschmidt${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Circ Nurse',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-09',
      name: 'Quinn Delacroix',
      email: `qdelacroix${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Scrub Tech',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-10',
      name: 'Harper Novak',
      email: `hnovak${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'RN',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-11',
      name: 'Rowan Fischer',
      email: `rfischer${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Scrub Tech',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-12',
      name: 'Emerson Vance',
      email: `evance${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Circ Nurse',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-13',
      name: 'Sasha Romano',
      email: `sromano${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'Scrub Tech',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
    {
      userId: 'demo-staff-14',
      name: 'Blake Sullivan',
      email: `bsullivan${DEMO_STAFF_EMAIL_DOMAIN}`,
      position: 'RN',
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: null,
      siToday: 0,
      siOffCase: 0,
    },
  ],
};

// The demo OR Overview shows 3 ongoing rooms and 2 completed
// rooms. The staff overlay reuses these room names to assign real staff
// members to "on duty" / "active earlier today" buckets so the Staff tab
// references the same rooms as the OR Overview tab. Surgeon names live on
// the OR cards only (inside getDemoOrStatus) and never leak into the staff
// list anymore — the staff list is built strictly from the real fetch.
const DEMO_ONGOING_ROOM_NAMES = ['OR 3', 'OR 7', 'OR 12'];
const DEMO_DONE_ROOM_NAMES = ['OR 1', 'OR 5'];

// room → most-recent procedure mapping. Keeps the staff
// card's "last procedure" tag in sync with the procedure rendered on the OR
// Overview card for that room. Inactive staff cycle through this list so
// every demo row shows a room + procedure instead of "—".
const DEMO_ROOM_TO_PROCEDURE: Record<string, string> = {
  'OR 3': 'Lumbar Microdiscectomy',
  'OR 7': 'ALIF L4-L5',
  'OR 12': 'Craniotomy',
  'OR 1': 'Rotator Cuff Repair',
  'OR 5': 'Cervical Laminoplasty',
};
// Paired pool — cycled through for inactive demo staff so both
// `lastRoomToday` and `lastProcedureName` come from the same pair and the
// card reads "Procedure • OR X" consistently.
const DEMO_ROOM_PROC_POOL: Array<{ room: string; procedure: string }> = Object.entries(
  DEMO_ROOM_TO_PROCEDURE
).map(([room, procedure]) => ({ room, procedure }));

const PHASES = [
  'Pre-Op Setup',
  'Time Out',
  'Incision',
  'Exposure',
  'Primary Procedure',
  'Hemostasis',
  'Closing',
  'Post-Op',
];

// ─── small helpers ────────────────────────────────────────────────
function isoMinutesAgo(min: number): string {
  return new Date(Date.now() - min * 60_000).toISOString();
}
function isoSecondsAgo(sec: number): string {
  return new Date(Date.now() - sec * 1000).toISOString();
}

// Hide staff members whose display name is a single word (e.g. "John") —
// those look like incomplete profiles on a demo screen. Stacked alongside the
// DEMO_STAFF_EMAIL_DOMAIN filter so the same trim/empty fallbacks apply.
function hasMultiWordName(name: string | null | undefined): boolean {
  if (!name) return false;
  return name.trim().split(/\s+/).length >= 2;
}

// Builds a steps array with phase indices, marking earlier-phase steps as
// done with click timestamps spread across the elapsed window, then the
// early steps of the current phase as done, and the rest as pending. The
// current step is marked but not done.
interface StepSpec {
  name: string;
  phase: number;
}
function buildSteps(
  specs: StepSpec[],
  currentPhase: number,
  currentStepName: string,
  caseStartedMinAgo: number
): CanopusStep[] {
  const total = specs.length;
  const out: CanopusStep[] = [];
  // Distribute completed-step timestamps evenly between caseStart and now.
  const startMs = Date.now() - caseStartedMinAgo * 60_000;
  const nowMs = Date.now();
  const totalToCurrent = specs.findIndex((s) => s.name === currentStepName);
  const completedCount = totalToCurrent === -1 ? Math.floor(total * 0.6) : totalToCurrent;
  const span = Math.max(1, nowMs - startMs);
  const tick = span / Math.max(1, completedCount + 1);
  for (let i = 0; i < specs.length; i++) {
    const spec = specs[i]!;
    const isCompleted = i < completedCount;
    out.push({
      name: spec.name,
      phase: PHASES[spec.phase] ?? 'Unknown',
      phaseIndex: spec.phase,
      source: 1,
      done: isCompleted,
      clickTimeIso: isCompleted ? new Date(startMs + tick * (i + 1)).toISOString() : null,
    });
  }
  return out;
}

function buildPhases(
  durations: Array<{ name: string; durationSec: number | null; avgSec: number | null }>,
  inProgressIdx: number
): CanopusPhaseTiming[] {
  return durations.map((d, i) => ({
    name: d.name,
    durationSec: d.durationSec,
    avgSec: d.avgSec,
    inProgress: i === inProgressIdx,
  }));
}

// ─── OR Status ────────────────────────────────────────────────────

// Each ongoing/completed slot has a *target specialty* and
// a *fallback specialty*. The real surgeon list from
// `insightsService.getFacilityInsights` is assigned to slots by specialty
// match. `surgeonName` and `profileName` are derived per-tick from the
// assigned surgeon's real name — never hardcoded.
interface OngoingCaseDescriptor {
  roomId: string;
  roomName: string;
  status: 'active' | 'long' | 'closing';
  procedureName: string;
  specialty: string;
  profileTail: string; // appended after the surgeon's last name to form profileName
  startedMinAgo: number;
  currentPhaseIndex: number;
  currentStepName: string;
  steps: StepSpec[];
  phaseDurations: Array<{ durationSec: number | null; avgSec: number | null }>;
  avgDurationSec: number;
  caseCount: number;
  pace: 'on' | 'slight' | 'over';
  runningLong: boolean;
  alerts: string[];
  // Team Familiarity Index displayed on the OR card (0..100).
  tfi: number;
  // Recording device label shown on the live OR Detail panel
  // (e.g. "iPad", "Apple Vision Pro").
  device: string;
}

const ONGOING: OngoingCaseDescriptor[] = [
  // OR 3 — Active, on-pace — Lumbar Microdiscectomy (Spine)
  {
    roomId: 'or-3',
    roomName: 'OR 3',
    status: 'active',
    procedureName: 'Lumbar Microdiscectomy',
    specialty: 'Spine',
    profileTail: 'Lumbar Micro Standard',
    startedMinAgo: 62,
    currentPhaseIndex: 4, // Primary Procedure
    currentStepName: 'Decompress nerve root',
    steps: [
      { name: 'Patient positioning', phase: 0 },
      { name: 'Skin prep and drape', phase: 0 },
      { name: 'Instrument table setup', phase: 0 },
      { name: 'Time out — confirm patient/site/procedure', phase: 1 },
      { name: 'Antibiotic confirmation', phase: 1 },
      { name: 'Skin incision', phase: 2 },
      { name: 'Subcutaneous dissection', phase: 2 },
      { name: 'Fascia incision', phase: 3 },
      { name: 'Paraspinal muscle retraction', phase: 3 },
      { name: 'Identify lamina', phase: 3 },
      { name: 'Partial laminotomy', phase: 4 },
      { name: 'Ligamentum flavum removal', phase: 4 },
      { name: 'Decompress nerve root', phase: 4 },
      { name: 'Discectomy', phase: 4 },
      { name: 'Irrigation and inspection', phase: 5 },
      { name: 'Hemostasis check', phase: 5 },
      { name: 'Fascia closure', phase: 6 },
      { name: 'Subcutaneous closure', phase: 6 },
      { name: 'Skin closure', phase: 6 },
      { name: 'Dressing application', phase: 7 },
      { name: 'Transfer to PACU', phase: 7 },
    ],
    phaseDurations: [
      { durationSec: 720, avgSec: 780 },
      { durationSec: 240, avgSec: 260 },
      { durationSec: 360, avgSec: 380 },
      { durationSec: 540, avgSec: 560 },
      { durationSec: null, avgSec: 1500 },
      { durationSec: null, avgSec: 360 },
      { durationSec: null, avgSec: 420 },
      { durationSec: null, avgSec: 180 },
    ],
    avgDurationSec: 4440, // 74 min historical avg
    caseCount: 32,
    pace: 'on',
    runningLong: false,
    alerts: [],
    tfi: 72,
    device: 'iPad',
  },
  // OR 7 — Running long — ALIF L4-L5 (Spine)
  {
    roomId: 'or-7',
    roomName: 'OR 7',
    status: 'long',
    procedureName: 'ALIF L4-L5',
    specialty: 'Spine',
    profileTail: 'ALIF Standard',
    startedMinAgo: 178,
    currentPhaseIndex: 4, // Primary Procedure (running long)
    currentStepName: 'Cage placement',
    steps: [
      { name: 'Patient positioning (supine)', phase: 0 },
      { name: 'Skin prep and drape', phase: 0 },
      { name: 'Vascular access ready', phase: 0 },
      { name: 'Time out — confirm patient/site/procedure', phase: 1 },
      { name: 'Antibiotic confirmation', phase: 1 },
      { name: 'Mini-laparotomy incision', phase: 2 },
      { name: 'Subcutaneous dissection', phase: 2 },
      { name: 'Retroperitoneal approach', phase: 3 },
      { name: 'Mobilize great vessels', phase: 3 },
      { name: 'Expose L4-L5 disc space', phase: 3 },
      { name: 'Discectomy', phase: 4 },
      { name: 'End-plate preparation', phase: 4 },
      { name: 'Trial sizing', phase: 4 },
      { name: 'Cage placement', phase: 4 },
      { name: 'Bone graft packing', phase: 4 },
      { name: 'Final imaging', phase: 4 },
      { name: 'Irrigation', phase: 5 },
      { name: 'Hemostasis check', phase: 5 },
      { name: 'Fascia closure', phase: 6 },
      { name: 'Subcutaneous closure', phase: 6 },
      { name: 'Skin closure', phase: 6 },
      { name: 'Dressing application', phase: 7 },
      { name: 'Transfer to PACU', phase: 7 },
    ],
    phaseDurations: [
      { durationSec: 900, avgSec: 840 },
      { durationSec: 300, avgSec: 280 },
      { durationSec: 600, avgSec: 540 },
      { durationSec: 1320, avgSec: 1200 },
      { durationSec: null, avgSec: 4800 },
      { durationSec: null, avgSec: 480 },
      { durationSec: null, avgSec: 600 },
      { durationSec: null, avgSec: 240 },
    ],
    avgDurationSec: 8880, // 148 min historical avg, currently at 178 = over by 30
    caseCount: 24,
    pace: 'over',
    runningLong: true,
    alerts: ['Running 30+ min over historical average'],
    tfi: 38,
    device: 'Apple Vision Pro',
  },
  // OR 12 — Closing — Craniotomy (Neurosurgery)
  {
    roomId: 'or-12',
    roomName: 'OR 12',
    status: 'closing',
    procedureName: 'Craniotomy',
    specialty: 'Neurosurgery',
    profileTail: 'Craniotomy Standard',
    startedMinAgo: 215,
    currentPhaseIndex: 6, // Closing
    currentStepName: 'Galea closure',
    steps: [
      { name: 'Patient positioning (Mayfield)', phase: 0 },
      { name: 'Head shave and prep', phase: 0 },
      { name: 'Navigation registration', phase: 0 },
      { name: 'Time out — confirm patient/site/procedure', phase: 1 },
      { name: 'Antibiotic confirmation', phase: 1 },
      { name: 'Scalp incision', phase: 2 },
      { name: 'Galea flap elevation', phase: 2 },
      { name: 'Burr holes', phase: 3 },
      { name: 'Craniotomy bone flap', phase: 3 },
      { name: 'Dura tack-up sutures', phase: 3 },
      { name: 'Dural opening', phase: 4 },
      { name: 'Microscope in', phase: 4 },
      { name: 'Tumor identification', phase: 4 },
      { name: 'Tumor resection', phase: 4 },
      { name: 'Intraoperative pathology', phase: 4 },
      { name: 'Final inspection', phase: 4 },
      { name: 'Hemostasis with bipolar', phase: 5 },
      { name: 'Irrigation', phase: 5 },
      { name: 'Dural closure', phase: 6 },
      { name: 'Bone flap reattachment', phase: 6 },
      { name: 'Galea closure', phase: 6 },
      { name: 'Skin closure (staples)', phase: 6 },
      { name: 'Dressing application', phase: 7 },
      { name: 'Transfer to NeuroICU', phase: 7 },
    ],
    phaseDurations: [
      { durationSec: 1080, avgSec: 1140 },
      { durationSec: 300, avgSec: 280 },
      { durationSec: 600, avgSec: 660 },
      { durationSec: 1620, avgSec: 1500 },
      { durationSec: 7200, avgSec: 7500 },
      { durationSec: 900, avgSec: 960 },
      { durationSec: null, avgSec: 1200 },
      { durationSec: null, avgSec: 240 },
    ],
    avgDurationSec: 13500, // 225 min, currently at 215 = on pace / slight
    caseCount: 28,
    pace: 'slight',
    runningLong: false,
    alerts: [],
    tfi: 85,
    device: 'iPad',
  },
];

// Completed-case templates — surgeon name comes from the
// real surgeon list at runtime, paired by specialty.
interface CompletedCaseDescriptor {
  roomId: string;
  roomName: string;
  procedureName: string;
  specialty: string;
  startedMinAgo: number;
  endedMinAgo: number;
  totalSteps: number;
  stepsCompleted: number;
  // Historical avg + sample size for this surgeon + procedure combo,
  // surfaced in the "Today's Recorded Cases" Avg (n) column.
  avgDurationSec: number;
  caseCount: number;
}

const COMPLETED: CompletedCaseDescriptor[] = [
  {
    roomId: 'or-1',
    roomName: 'OR 1',
    procedureName: 'Rotator Cuff Repair',
    specialty: 'Orthopedics',
    startedMinAgo: 320,
    endedMinAgo: 180,
    totalSteps: 18,
    stepsCompleted: 18,
    avgDurationSec: 7800, // 130 min
    caseCount: 22,
  },
  {
    roomId: 'or-5',
    roomName: 'OR 5',
    procedureName: 'Cervical Laminoplasty',
    specialty: 'Spine',
    startedMinAgo: 360,
    endedMinAgo: 95,
    totalSteps: 22,
    stepsCompleted: 22,
    avgDurationSec: 16200, // 270 min
    caseCount: 19,
  },
];

const IDLE_ROOM_NUMBERS = [2, 4, 6, 8, 9, 10, 11, 13, 14, 15, 16, 17];

// Deterministic per-slot off-case SI counts for the up-to-6
// on-duty staff. Used by both `applyDemoStaffOverlay` (Staff tab) and
// `getDemoSITfiRows` (SI × TFI Correlation table) so the two views always
// report the same off-case number for the same person. Values are
// intentionally non-zero so the column reads as meaningful.
const DEMO_SI_OFFCASE_BY_SLOT = [4, 6, 3, 7, 5, 8];

// Derive a "Surgeon — Procedure Tail" profile name from
// the assigned surgeon's real last name. Falls back to the full name if no
// space is present.
function buildProfileName(surgeonName: string, tail: string): string {
  const parts = surgeonName.trim().split(/\s+/);
  const last = parts.length > 1 ? parts[parts.length - 1]! : surgeonName.trim();
  return `${last} — ${tail}`;
}

function buildActiveCase(o: OngoingCaseDescriptor, surgeon: InsightsSurgeon): CanopusActiveCase {
  const elapsedSec = o.startedMinAgo * 60;
  const steps = buildSteps(o.steps, o.currentPhaseIndex, o.currentStepName, o.startedMinAgo);
  const phases = buildPhases(
    o.phaseDurations.map((d, i) => ({
      name: PHASES[i] ?? `Phase ${i}`,
      durationSec: d.durationSec,
      avgSec: d.avgSec,
    })),
    o.currentPhaseIndex
  );
  const stepsCompleted = steps.filter((s) => s.done).length;
  const currentStepIndex = Math.max(
    0,
    steps.findIndex((s) => s.name === o.currentStepName)
  );
  // Estimate remaining time: if running long, give a small positive number;
  // if on pace, base it on avg minus elapsed; if closing, just a few minutes.
  let estRemainingSec: number;
  if (o.status === 'closing') {
    estRemainingSec = 8 * 60;
  } else if (o.runningLong) {
    estRemainingSec = 25 * 60;
  } else {
    estRemainingSec = Math.max(15 * 60, o.avgDurationSec - elapsedSec);
  }
  return {
    procedureId: `proc-${o.roomId}`,
    surgeonId: surgeon.id,
    surgeonName: surgeon.name,
    procedureName: o.procedureName,
    profileName: buildProfileName(surgeon.name, o.profileTail),
    specialty: o.specialty,
    startTimeIso: isoMinutesAgo(o.startedMinAgo),
    elapsedSec,
    currentPhaseIndex: o.currentPhaseIndex,
    currentPhaseName: PHASES[o.currentPhaseIndex] ?? null,
    currentStepName: o.currentStepName,
    currentStepIndex,
    stepsCompleted,
    totalSteps: steps.length,
    hasAvg: true,
    caseCount: o.caseCount,
    avgDurationSec: o.avgDurationSec,
    estRemainingSec,
    pace: o.pace,
    runningLong: o.runningLong,
    alerts: o.alerts,
    phases,
    steps,
    participantIds: [`scrub-${o.roomId}`, `circ-${o.roomId}`],
    // Set by getDemoOrStatus once the on-duty staff round-robin runs.
    scrubTechName: null,
    tfi: o.tfi,
    deviceName: o.device,
    trackingStale: false,
    lastActivityIso: isoSecondsAgo(45),
  };
}

function buildOngoingRoom(o: OngoingCaseDescriptor, surgeon: InsightsSurgeon): CanopusRoomStatus {
  return {
    id: o.roomId,
    name: o.roomName,
    status: o.status,
    activeCase: buildActiveCase(o, surgeon),
    lastCase: null,
  };
}

function buildCompletedRoom(
  c: CompletedCaseDescriptor,
  surgeon: InsightsSurgeon
): CanopusRoomStatus {
  return {
    id: c.roomId,
    name: c.roomName,
    status: 'done',
    activeCase: null,
    lastCase: {
      procedureName: c.procedureName,
      surgeonName: surgeon.name,
      endTimeIso: isoMinutesAgo(c.endedMinAgo),
    },
  };
}

function buildIdleRoom(n: number): CanopusRoomStatus {
  return {
    id: `or-${n}`,
    name: `OR ${n}`,
    status: 'idle',
    activeCase: null,
    lastCase: null,
  };
}

// Deterministically pick up to 5 real surgeons (sorted by
// id) and pair each demo case slot (3 ongoing + 2 completed) with a surgeon.
// Pairing is specialty-aware: each slot first tries to match a surgeon
// whose specialty (case-insensitive substring match) aligns with the slot's
// target specialty; if no match remains, we fall back to round-robin from
// the unused pool. Each picked surgeon is consumed exactly once across the
// 5 slots, so no surgeon appears twice.
//
// Returns `null` when there aren't enough surgeons to fill even one slot —
// callers should treat that as "all rooms idle".
interface SurgeonAssignment {
  ongoing: Array<InsightsSurgeon | null>; // index-aligned with ONGOING
  completed: Array<InsightsSurgeon | null>; // index-aligned with COMPLETED
}
function assignSurgeons(realSurgeons: InsightsSurgeon[]): SurgeonAssignment {
  // Sort by id for stable ordering across reloads, then cap at 5.
  const pool = realSurgeons
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .slice(0, 5);

  // Slot list in priority order: 3 ongoing first, then 2 completed.
  const slots: Array<{ kind: 'ongoing' | 'completed'; idx: number; specialty: string }> = [
    ...ONGOING.map((o, idx) => ({ kind: 'ongoing' as const, idx, specialty: o.specialty })),
    ...COMPLETED.map((c, idx) => ({ kind: 'completed' as const, idx, specialty: c.specialty })),
  ];

  const used = new Set<string>();
  const ongoing: Array<InsightsSurgeon | null> = ONGOING.map(() => null);
  const completed: Array<InsightsSurgeon | null> = COMPLETED.map(() => null);

  // Pass 1: specialty match.
  for (const slot of slots) {
    const match = pool.find(
      (s) =>
        !used.has(s.id) &&
        s.specialty &&
        s.specialty.toLowerCase().includes(slot.specialty.toLowerCase())
    );
    if (match) {
      used.add(match.id);
      if (slot.kind === 'ongoing') ongoing[slot.idx] = match;
      else completed[slot.idx] = match;
    }
  }
  // Pass 2: round-robin from the unused pool for any still-empty slots.
  const leftover = pool.filter((s) => !used.has(s.id));
  let cursor = 0;
  for (const slot of slots) {
    const target = slot.kind === 'ongoing' ? ongoing : completed;
    if (target[slot.idx] !== null) continue;
    const next = leftover[cursor++];
    if (!next) break;
    used.add(next.id);
    target[slot.idx] = next;
  }
  return { ongoing, completed };
}

// Build a Map of ongoing-room → demo-assigned Scrub Tech name using the same
// filter/sort/round-robin logic as applyDemoStaffOverlay (demo-domain emails,
// multi-word names, deterministic order by userId, first staff slot per room).
// Keeps the OR Overview Detail's Scrub Tech in sync with the Staff tab's
// on-duty list.
function buildDemoScrubTechByRoom(real: CanopusStaffStatus | null): Map<string, string> {
  const map = new Map<string, string>();
  if (!real) return map;
  const demoStaffOnly = real.members.filter(
    (m) =>
      (m.email ?? '').toLowerCase().endsWith(DEMO_STAFF_EMAIL_DOMAIN) && hasMultiWordName(m.name)
  );
  const ordered = demoStaffOnly.slice().sort((a, b) => a.userId.localeCompare(b.userId));
  const onDutyTarget = Math.min(6, ordered.length);
  // First staff at each round-robin slot 0..2 becomes the Scrub Tech for
  // OR 3 / OR 7 / OR 12 respectively. Slots 3..5 (the second pass) are
  // ignored here — we only need one Scrub Tech per room.
  for (let idx = 0; idx < onDutyTarget && idx < DEMO_ONGOING_ROOM_NAMES.length; idx++) {
    const roomName = DEMO_ONGOING_ROOM_NAMES[idx]!;
    const name = ordered[idx]?.name;
    if (name) map.set(roomName, name);
  }
  return map;
}

// Synchronous fixture builder; safe to call every poll
// tick. Times are computed from `Date.now()` so durations always look
// fresh. Surgeon names come from `realSurgeons` — never hardcoded. If
// `realSurgeons` is null/empty, all rooms render idle. `realStaff` is
// only used to derive a Scrub Tech display name per ongoing room.
export function getDemoOrStatus(
  realSurgeons: InsightsSurgeon[] | null,
  realStaff: CanopusStaffStatus | null = null,
  facilityName: string = DEFAULT_DEMO_FACILITY_NAME
): CanopusOrStatus {
  const hasSurgeons = !!(realSurgeons && realSurgeons.length > 0);
  const assignment: SurgeonAssignment | null = hasSurgeons ? assignSurgeons(realSurgeons!) : null;
  const scrubByRoom = buildDemoScrubTechByRoom(realStaff);

  const rooms: CanopusRoomStatus[] = [];
  // Build all 17 in numeric order so the UI sort is a no-op.
  for (let n = 1; n <= 17; n++) {
    const ongoingIdx = ONGOING.findIndex((o) => o.roomId === `or-${n}`);
    if (ongoingIdx !== -1) {
      const surgeon = assignment?.ongoing[ongoingIdx] ?? null;
      if (surgeon) {
        const room = buildOngoingRoom(ONGOING[ongoingIdx]!, surgeon);
        if (room.activeCase) {
          room.activeCase.scrubTechName = scrubByRoom.get(room.name) ?? null;
        }
        rooms.push(room);
      } else {
        // No surgeon assigned — render this room idle so we don't show a fake name.
        rooms.push(buildIdleRoom(n));
      }
      continue;
    }
    const completedIdx = COMPLETED.findIndex((c) => c.roomId === `or-${n}`);
    if (completedIdx !== -1) {
      const surgeon = assignment?.completed[completedIdx] ?? null;
      if (surgeon) {
        rooms.push(buildCompletedRoom(COMPLETED[completedIdx]!, surgeon));
      } else {
        rooms.push(buildIdleRoom(n));
      }
      continue;
    }
    if (IDLE_ROOM_NUMBERS.includes(n)) {
      rooms.push(buildIdleRoom(n));
      continue;
    }
  }

  // Today's cases — 3 ongoing (endTimeIso null) + 2 completed. Skip slots
  // without an assigned real surgeon.
  const todaysCases = [
    ...ONGOING.flatMap((o, idx) => {
      const surgeon = assignment?.ongoing[idx];
      if (!surgeon) return [];
      return [
        {
          procedureId: `proc-${o.roomId}`,
          surgeonName: surgeon.name,
          procedureName: o.procedureName,
          roomName: o.roomName,
          startTimeIso: isoMinutesAgo(o.startedMinAgo),
          endTimeIso: null,
          totalTimeSec: o.startedMinAgo * 60,
          stepsCompleted: Math.floor(o.steps.length * 0.6),
          totalSteps: o.steps.length,
          avgDurationSec: o.avgDurationSec,
          caseCount: o.caseCount,
        },
      ];
    }),
    ...COMPLETED.flatMap((c, idx) => {
      const surgeon = assignment?.completed[idx];
      if (!surgeon) return [];
      return [
        {
          procedureId: `proc-${c.roomId}`,
          surgeonName: surgeon.name,
          procedureName: c.procedureName,
          roomName: c.roomName,
          startTimeIso: isoMinutesAgo(c.startedMinAgo),
          endTimeIso: isoMinutesAgo(c.endedMinAgo),
          totalTimeSec: (c.startedMinAgo - c.endedMinAgo) * 60,
          stepsCompleted: c.stepsCompleted,
          totalSteps: c.totalSteps,
          avgDurationSec: c.avgDurationSec,
          caseCount: c.caseCount,
        },
      ];
    }),
  ];

  // Events — only emit events for rooms whose ongoing slot has an assigned
  // surgeon (otherwise the room is rendered idle and an event would be a
  // ghost).
  const assignedRoomNames = new Set<string>(
    ONGOING.flatMap((o, idx) => (assignment?.ongoing[idx] ? [o.roomName] : []))
  );
  const events = [
    { roomName: 'OR 3', stepName: 'Decompress nerve root', minAgo: 2, phase: 'Primary Procedure' },
    { roomName: 'OR 7', stepName: 'Cage placement', minAgo: 4, phase: 'Primary Procedure' },
    { roomName: 'OR 12', stepName: 'Galea closure', minAgo: 6, phase: 'Closing' },
    {
      roomName: 'OR 3',
      stepName: 'Ligamentum flavum removal',
      minAgo: 12,
      phase: 'Primary Procedure',
    },
    { roomName: 'OR 12', stepName: 'Bone flap reattachment', minAgo: 18, phase: 'Closing' },
    { roomName: 'OR 7', stepName: 'Trial sizing', minAgo: 22, phase: 'Primary Procedure' },
    { roomName: 'OR 3', stepName: 'Partial laminotomy', minAgo: 28, phase: 'Primary Procedure' },
    { roomName: 'OR 12', stepName: 'Dural closure', minAgo: 35, phase: 'Closing' },
    { roomName: 'OR 7', stepName: 'End-plate preparation', minAgo: 42, phase: 'Primary Procedure' },
    { roomName: 'OR 3', stepName: 'Fascia incision', minAgo: 51, phase: 'Exposure' },
  ]
    .filter((e) => assignedRoomNames.has(e.roomName))
    .map((e) => ({
      timestampIso: isoMinutesAgo(e.minAgo),
      roomName: e.roomName,
      procedureName: ONGOING.find((o) => o.roomName === e.roomName)?.procedureName ?? '',
      stepName: e.stepName,
      phase: e.phase,
    }));

  return {
    facilityName,
    generatedAtIso: new Date().toISOString(),
    rooms,
    todaysCases,
    events,
  };
}

// ─── Staff ────────────────────────────────────────────────────────

// Overlay function — takes the real staff response from
// `canopusCommandService.getStaffStatus()` and returns a copy where only the
// status fields (onDuty / currentRoom / wasActiveEarlierToday / lastRoomToday
// / siToday / siOffCase) are rewritten with dummy values. The members
// themselves (userId, name, email) come straight from the real response —
// nobody is synthesized, renamed, or removed.
//
// Bucket sizes are capped by the real list size. With a typical demo staff
// list we get ~6 on-duty across OR 3 / OR 7 / OR 12 and ~5 active-earlier
// across OR 1 / OR 5; with a very small list (e.g. 3 members) we just fill
// what we can — that's fine, the rest of the demo doesn't depend on exact
// counts.
//
// Determinism: real members are sorted by userId (stable across reloads)
// before bucketing; SI counts are derived from index position.
export function applyDemoStaffOverlay(real: CanopusStaffStatus | null): CanopusStaffStatus {
  if (!real) {
    // fallback when the real staff fetch fails. We can't
    // populate names, so just return an empty member list — the Staff tab
    // will render "no staff" sections rather than crash. console.warn so
    // we notice during the demo.
    // eslint-disable-next-line no-console
    console.warn(
      '[demo-mode] applyDemoStaffOverlay called with null real staff; returning empty overlay'
    );
    return {
      generatedAtIso: new Date().toISOString(),
      members: [],
    };
  }

  // Filter to the demo staff pool — keep only members whose email ends with
  // DEMO_STAFF_EMAIL_DOMAIN (case-insensitive). Also drop members whose name
  // is a single word (incomplete profiles like "John" with no last name look
  // like fake entries on a demo screen).
  const demoStaffOnly = real.members.filter(
    (m) =>
      (m.email ?? '').toLowerCase().endsWith(DEMO_STAFF_EMAIL_DOMAIN) && hasMultiWordName(m.name)
  );

  // Deterministic order — sort real members by userId so the
  // same person always lands in the same bucket across reloads.
  const ordered = demoStaffOnly.slice().sort((a, b) => a.userId.localeCompare(b.userId));

  // Bucket sizes — cap by real list size so we never invent
  // anyone. Up to 6 on-duty distributed across the 3 ongoing rooms; up to 5
  // active-earlier distributed across the 2 completed rooms.
  const onDutyTarget = Math.min(6, ordered.length);
  const activeEarlierTarget = Math.min(5, Math.max(0, ordered.length - onDutyTarget));

  const onDutyMembers = ordered.slice(0, onDutyTarget);
  const activeEarlierMembers = ordered.slice(onDutyTarget, onDutyTarget + activeEarlierTarget);
  const inactiveMembers = ordered.slice(onDutyTarget + activeEarlierTarget);

  const out: CanopusStaffMember[] = [];

  // round-robin on-duty members across ongoing rooms.
  onDutyMembers.forEach((m, idx) => {
    const roomName = DEMO_ONGOING_ROOM_NAMES[idx % DEMO_ONGOING_ROOM_NAMES.length]!;
    out.push({
      ...m,
      onDuty: true,
      currentRoom: roomName,
      wasActiveEarlierToday: false,
      lastRoomToday: null,
      lastProcedureName: DEMO_ROOM_TO_PROCEDURE[roomName] ?? null,
      // Deterministic SI numbers in the 8-18 range for active staff.
      siToday: 8 + ((idx * 3) % 11),
      siOffCase: DEMO_SI_OFFCASE_BY_SLOT[idx % DEMO_SI_OFFCASE_BY_SLOT.length] ?? 5,
    });
  });

  // round-robin active-earlier members across completed rooms.
  activeEarlierMembers.forEach((m, idx) => {
    const roomName = DEMO_DONE_ROOM_NAMES[idx % DEMO_DONE_ROOM_NAMES.length]!;
    out.push({
      ...m,
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: true,
      lastRoomToday: roomName,
      lastProcedureName: DEMO_ROOM_TO_PROCEDURE[roomName] ?? null,
      siToday: 5 + ((idx * 3) % 8),
      siOffCase: idx % 3,
    });
  });

  // everyone else is inactive with zeroed status. We still
  // mock a "most recent procedure" + room so the Staff card has something to
  // show in place of the role label and OR tag — cycle through the demo pool
  // deterministically. `wasActiveEarlierToday` stays false so the partition
  // logic still places these members in the "Not Active" bucket; the room is
  // surfaced via `lastRoomToday` purely as a historical marker.
  inactiveMembers.forEach((m, idx) => {
    const pair = DEMO_ROOM_PROC_POOL[idx % DEMO_ROOM_PROC_POOL.length];
    out.push({
      ...m,
      onDuty: false,
      currentRoom: null,
      wasActiveEarlierToday: false,
      lastRoomToday: pair?.room ?? null,
      lastProcedureName: pair?.procedure ?? null,
      siToday: 0,
      siOffCase: 0,
    });
  });

  return {
    generatedAtIso: new Date().toISOString(),
    members: out,
  };
}

// ─── Surgical Inquiries ───────────────────────────────────────────

// Base "today" totals; other periods scale this by a multiplier.
const TODAY_TOTAL = 142;

const PERIOD_MULTIPLIER: Record<CanopusSIPeriod, number> = {
  today: 1,
  '7d': 6,
  '30d': 22,
  '90d': 65,
  ytd: 150,
  all: 400,
};

// accepts the real staff and surgeon lists so all
// surgeon-keyed sections (byOR, bySurgeon, byProfile) and the staff-keyed
// byStaff section can be populated with real names instead of synthesized
// ones. When realSurgeons is null/empty, the surgeon-keyed sections are
// returned empty. When realStaff is null/empty, byStaff is empty.
export function getDemoSIAggregates(
  period: CanopusSIPeriod,
  realStaff: CanopusStaffMember[] | null = null,
  realSurgeons: InsightsSurgeon[] | null = null
): CanopusSIAggregates {
  const mult = PERIOD_MULTIPLIER[period];
  const scale = (n: number) => Math.round(n * mult);

  const total = scale(TODAY_TOTAL);
  const casesCount = period === 'today' ? 5 : Math.round(5 * mult * 0.9);
  const offCase = scale(25);

  // Module distribution — refGuide largest, then pickList, then notes, tray, room.
  const byModule = {
    refGuide: scale(58),
    pickList: scale(32),
    procNotes: scale(24),
    trayLayout: scale(18),
    roomLayout: scale(10),
  };

  // Pair real surgeons against the same 5 demo case slots
  // used for the OR Overview tab, by specialty. The result aligns byOR /
  // byProfile / bySurgeon to the same 5 surgeons that appear on the OR
  // cards, which is what the user expects to see end-to-end.
  const assignment: SurgeonAssignment | null =
    realSurgeons && realSurgeons.length > 0 ? assignSurgeons(realSurgeons) : null;

  // Static per-slot SI numbers in the same order as ONGOING + COMPLETED.
  // Index 0..2 = ongoing (OR 3/7/12), index 3..4 = completed (OR 1/5).
  const OR_SLOT_NUMBERS = [
    {
      total: 38,
      mods: { refGuide: 16, pickList: 9, trayLayout: 5, roomLayout: 3, procNotes: 5 },
      liveCase: true,
    },
    {
      total: 42,
      mods: { refGuide: 18, pickList: 10, trayLayout: 5, roomLayout: 2, procNotes: 7 },
      liveCase: true,
    },
    {
      total: 32,
      mods: { refGuide: 14, pickList: 7, trayLayout: 4, roomLayout: 2, procNotes: 5 },
      liveCase: true,
    },
    {
      total: 15,
      mods: { refGuide: 6, pickList: 4, trayLayout: 2, roomLayout: 1, procNotes: 2 },
      liveCase: false,
    },
    {
      total: 15,
      mods: { refGuide: 4, pickList: 2, trayLayout: 2, roomLayout: 2, procNotes: 5 },
      liveCase: false,
    },
  ];

  // Per-OR distribution — only the 5 active/done rooms have today's traffic.
  // Each row's surgeon name comes from the assignment (real); slots without
  // an assigned surgeon are omitted.
  const byOR = [
    ...ONGOING.map((o, idx) => ({
      slot: idx,
      roomId: o.roomId,
      roomName: o.roomName,
      procedureName: o.procedureName,
      surgeon: assignment?.ongoing[idx] ?? null,
      numbers: OR_SLOT_NUMBERS[idx]!,
    })),
    ...COMPLETED.map((c, idx) => ({
      slot: ONGOING.length + idx,
      roomId: c.roomId,
      roomName: c.roomName,
      procedureName: c.procedureName,
      surgeon: assignment?.completed[idx] ?? null,
      numbers: OR_SLOT_NUMBERS[ONGOING.length + idx]!,
    })),
  ]
    .filter((row) => row.surgeon !== null)
    .map((row) => {
      // synthesize per-OR cases / avgPerCase / topModule.
      // For "today" each OR has 1 case; for other periods scale a per-OR
      // baseline (~14..18 cases / 5 days) by the period multiplier. Top
      // module is whichever count is highest among the OR's modules.
      const perORBaselineCases = 14 + ((row.slot * 2) % 5);
      const cases =
        period === 'today' ? 1 : Math.max(1, Math.round((perORBaselineCases * mult) / 5));
      const totalScaled = scale(row.numbers.total);
      const avgPerCase = cases > 0 ? Math.round((totalScaled / cases) * 10) / 10 : 0;
      const modEntries: Array<[string, number]> = [
        ['Reference Guide', row.numbers.mods.refGuide],
        ['Pick List', row.numbers.mods.pickList],
        ['Tray Layout', row.numbers.mods.trayLayout],
        ['Room Layout', row.numbers.mods.roomLayout],
        ['Procedure Notes', row.numbers.mods.procNotes],
      ];
      const topModule = modEntries.reduce((best, cur) => (cur[1] > best[1] ? cur : best))[0];
      return {
        roomId: row.roomId,
        roomName: row.roomName,
        total: totalScaled,
        surgeonName: row.surgeon!.name,
        procedureName: row.procedureName,
        liveCase: row.numbers.liveCase,
        modules: {
          refGuide: scale(row.numbers.mods.refGuide),
          pickList: scale(row.numbers.mods.pickList),
          trayLayout: scale(row.numbers.mods.trayLayout),
          roomLayout: scale(row.numbers.mods.roomLayout),
          procNotes: scale(row.numbers.mods.procNotes),
        },
        cases,
        avgPerCase,
        topModule,
      };
    });

  // byStaff rows use real staff names only. Up to 8 entries
  // pulled deterministically from the real staff list (sorted by userId).
  // Dummy totals/offCase/topModule are assigned per row. Empty when no real
  // staff is available.
  const SI_TOP_MODULES = [
    'Reference Guide',
    'Reference Guide',
    'Pick List',
    'Reference Guide',
    'Procedure Notes',
    'Reference Guide',
    'Tray Layout',
    'Pick List',
  ];
  const SI_BASE_TOTALS = [28, 24, 22, 20, 18, 17, 14, 13];
  const SI_BASE_OFF = [2, 2, 3, 1, 2, 3, 2, 4];
  // per-slot case counts for the SI byStaff leaderboard.
  // Combined with SI_BASE_TOTALS (scaled to period) these produce
  // believable avgPerCase values (~2..4 SIs per case for active staff).
  const SI_BASE_CASES_TODAY = [3, 2, 2, 2, 1, 2, 1, 1];
  // Filter to the demo staff pool — same DEMO_STAFF_EMAIL_DOMAIN + multi-word
  // name rules that `applyDemoStaffOverlay` uses for the Staff tab, so the SI
  // byStaff leaders are drawn from the same pool.
  const byStaff = (realStaff ?? [])
    .filter(
      (m) =>
        (m.email ?? '').toLowerCase().endsWith(DEMO_STAFF_EMAIL_DOMAIN) && hasMultiWordName(m.name)
    )
    .slice()
    .sort((a, b) => a.userId.localeCompare(b.userId))
    .slice(0, 8)
    .map((m, idx) => {
      const total = scale(SI_BASE_TOTALS[idx] ?? 10);
      const cases =
        period === 'today'
          ? (SI_BASE_CASES_TODAY[idx] ?? 1)
          : Math.max(1, Math.round(((SI_BASE_CASES_TODAY[idx] ?? 1) * mult * 3) / 1));
      const avgPerCase = cases > 0 ? Math.round((total / cases) * 10) / 10 : 0;
      return {
        userId: m.userId,
        name: m.name ?? m.userId,
        // TODO(temp-role): demo members don't have a `position` set, so this
        // forces "Scrub Tech" for the demo SI / Staff views. Drop once every
        // demo member carries a real position.
        position: 'Scrub Tech',
        total,
        offCase: scale(SI_BASE_OFF[idx] ?? 1),
        topModule: SI_TOP_MODULES[idx] ?? 'Reference Guide',
        cases,
        avgPerCase,
      };
    });

  // bySurgeon rows come straight from the real surgeon
  // list (deterministic top 8 by id). Totals/cases are static per-rank
  // dummy values. Empty when no real surgeons are available.
  const BY_SURGEON_TOTALS = [42, 38, 32, 28, 22, 18, 15, 12];
  const BY_SURGEON_CASES_BASE = [28, 34, 22, 18, 16, 14, 12, 10];
  const BY_SURGEON_TOP_MODULE = [
    'Reference Guide',
    'Reference Guide',
    'Reference Guide',
    'Pick List',
    'Procedure Notes',
    'Reference Guide',
    'Pick List',
    'Tray Layout',
  ];
  const bySurgeon = (realSurgeons ?? [])
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .slice(0, 8)
    .map((s, idx) => {
      const baseTotal = BY_SURGEON_TOTALS[idx] ?? 10;
      const baseCases = BY_SURGEON_CASES_BASE[idx] ?? 8;
      return {
        surgeonId: s.id,
        name: s.name,
        // TODO(temp-specialty): map raw specialty variant strings onto the
        // cleaner demo labels. Drop once the underlying data is cleaned up.
        specialty: normalizeDemoSpecialty(s.specialty),
        total: scale(baseTotal),
        cases: period === 'today' ? 1 : Math.round((baseCases * mult) / 5),
        avgPerCase: baseTotal,
        topModule: BY_SURGEON_TOP_MODULE[idx] ?? 'Reference Guide',
      };
    });

  const bySpecialty = [
    {
      name: 'Spine',
      total: scale(95),
      cases: period === 'today' ? 3 : Math.round((78 * mult) / 5),
      avgPerCase: 32,
      topModule: 'Reference Guide',
    },
    {
      name: 'Neurosurgery',
      total: scale(32),
      cases: period === 'today' ? 1 : Math.round((22 * mult) / 5),
      avgPerCase: 32,
      topModule: 'Reference Guide',
    },
    {
      name: 'Orthopedics',
      total: scale(15),
      cases: period === 'today' ? 1 : Math.round((18 * mult) / 5),
      avgPerCase: 15,
      topModule: 'Pick List',
    },
  ];

  const byProcedure = [
    {
      name: 'ALIF L4-L5',
      specialty: 'Spine',
      total: scale(42),
      cases: period === 'today' ? 1 : Math.round((14 * mult) / 5),
      avgPerCase: 42,
      topModule: 'Reference Guide',
    },
    {
      name: 'Lumbar Microdiscectomy',
      specialty: 'Spine',
      total: scale(38),
      cases: period === 'today' ? 1 : Math.round((20 * mult) / 5),
      avgPerCase: 38,
      topModule: 'Reference Guide',
    },
    {
      name: 'Craniotomy',
      specialty: 'Neurosurgery',
      total: scale(32),
      cases: period === 'today' ? 1 : Math.round((18 * mult) / 5),
      avgPerCase: 32,
      topModule: 'Reference Guide',
    },
    {
      name: 'Rotator Cuff Repair',
      specialty: 'Orthopedics',
      total: scale(15),
      cases: period === 'today' ? 1 : Math.round((18 * mult) / 5),
      avgPerCase: 15,
      topModule: 'Pick List',
    },
    {
      name: 'Cervical Laminoplasty',
      specialty: 'Spine',
      total: scale(15),
      cases: period === 'today' ? 1 : Math.round((14 * mult) / 5),
      avgPerCase: 15,
      topModule: 'Procedure Notes',
    },
  ];

  // byProfile rows derive their surgeon name from the
  // assignment (same surgeon-to-slot mapping as byOR / OR Overview), and
  // the profile name is built from the surgeon's last name + the slot's
  // profile tail. Slots without an assigned real surgeon are omitted.
  const PROFILE_SLOTS = [
    {
      idx: 0,
      kind: 'ongoing' as const,
      tail: ONGOING[0]!.profileTail,
      procedureName: ONGOING[0]!.procedureName,
      total: 42,
      cases: 14,
      modules: { refGuide: 18, pickList: 10, trayLayout: 5, roomLayout: 2, procNotes: 7 },
    },
    {
      idx: 1,
      kind: 'ongoing' as const,
      tail: ONGOING[1]!.profileTail,
      procedureName: ONGOING[1]!.procedureName,
      total: 38,
      cases: 20,
      modules: { refGuide: 16, pickList: 9, trayLayout: 5, roomLayout: 3, procNotes: 5 },
    },
    {
      idx: 2,
      kind: 'ongoing' as const,
      tail: ONGOING[2]!.profileTail,
      procedureName: ONGOING[2]!.procedureName,
      total: 32,
      cases: 18,
      modules: { refGuide: 14, pickList: 7, trayLayout: 4, roomLayout: 2, procNotes: 5 },
    },
    {
      idx: 0,
      kind: 'completed' as const,
      tail: 'Rotator Cuff',
      procedureName: COMPLETED[0]!.procedureName,
      total: 15,
      cases: 18,
      modules: { refGuide: 6, pickList: 4, trayLayout: 2, roomLayout: 1, procNotes: 2 },
    },
    {
      idx: 1,
      kind: 'completed' as const,
      tail: 'Cervical Lami',
      procedureName: COMPLETED[1]!.procedureName,
      total: 15,
      cases: 14,
      modules: { refGuide: 4, pickList: 2, trayLayout: 2, roomLayout: 2, procNotes: 5 },
    },
  ];
  const byProfile = PROFILE_SLOTS.flatMap((slot, i) => {
    const surgeon =
      slot.kind === 'ongoing'
        ? (assignment?.ongoing[slot.idx] ?? null)
        : (assignment?.completed[slot.idx] ?? null);
    if (!surgeon) return [];
    return [
      {
        prefCardId: `pc-${i + 1}`,
        name: buildProfileName(surgeon.name, slot.tail),
        surgeonName: surgeon.name,
        procedureName: slot.procedureName,
        total: scale(slot.total),
        cases: period === 'today' ? 1 : Math.round((slot.cases * mult) / 5),
        avgPerCase: slot.total,
        modules: {
          refGuide: scale(slot.modules.refGuide),
          pickList: scale(slot.modules.pickList),
          trayLayout: scale(slot.modules.trayLayout),
          roomLayout: scale(slot.modules.roomLayout),
          procNotes: scale(slot.modules.procNotes),
        },
      },
    ];
  });

  const emptyModules = { refGuide: 0, pickList: 0, trayLayout: 0, roomLayout: 0, procNotes: 0 };
  const heatmap = [
    { day: 'Mon', total: scale(135), modules: emptyModules },
    { day: 'Tue', total: scale(148), modules: emptyModules },
    { day: 'Wed', total: scale(156), modules: emptyModules },
    { day: 'Thu', total: scale(142), modules: emptyModules },
    { day: 'Fri', total: scale(162), modules: emptyModules },
    { day: 'Sat', total: scale(48), modules: emptyModules },
    { day: 'Sun', total: scale(22), modules: emptyModules },
  ];

  // Monthly trend — 6 months ending in current month. Show growth.
  const now = new Date();
  const monthlyTrend = Array.from({ length: 6 }).map((_, idx) => {
    const monthOffset = 5 - idx;
    const d = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
    const label = `${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}`;
    // Growth curve: 2.0k -> 3.6k SIs/month, scaled by period mult-ish (use 1).
    const monthTotal = Math.round((2000 + idx * 320) * (mult === 1 ? 1 : 1));
    const offCaseM = Math.round(monthTotal * 0.17);
    const cases = Math.round(monthTotal / 28);
    return {
      month: label,
      total: monthTotal,
      offCase: offCaseM,
      cases,
      avgPerCase: 28,
    };
  });

  // Timing — pct values that sum to ~100.
  const timing = {
    intraOperative: { count: scale(74), pct: 52 },
    preOperative: { count: scale(28), pct: 20 },
    postOperative: { count: scale(15), pct: 10 },
    outsideSurgery: { count: scale(25), pct: 18 },
  };

  return {
    period,
    generatedAtIso: new Date().toISOString(),
    overview: {
      total,
      avgPerCase: period === 'today' ? 28 : 27,
      cases: casesCount,
      liveCaseRatio: 0.65,
      offCase,
    },
    byModule,
    byOR,
    byStaff,
    byProfile,
    bySurgeon,
    bySpecialty,
    byProcedure,
    heatmap,
    monthlyTrend,
    timing,
  };
}

// ─── SI × TFI Correlation (demo) ──────────────────────────────────

// Dummy TFI / Layers / Expected rows for the SI × TFI
// Correlation card. We have no real TFI data yet, so in demo mode we
// synthesize deterministic values from the on-duty demo staff list (same
// pool the Staff tab uses via `applyDemoStaffOverlay`). Delete with the
// rest of the demo wiring once a real TFI source is wired up.
export interface DemoSITfiRow {
  name: string;
  tfi: number;
  layers: [number, number, number];
  siToday: number;
  siOffCase: number;
  expected: number | null;
  remainingForBenchmark: number | null;
}

function clamp01_100(n: number): number {
  if (n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n);
}

// Builds rows for the on-duty demo subset (same first-6
// slice that `applyDemoStaffOverlay` uses for on-duty) and assigns dummy
// TFI / layers / expected / remaining values per row. The siToday /
// siOffCase numbers match `applyDemoStaffOverlay` exactly so the Staff tab
// and the SI × TFI table report the same counts for the same people.
export function getDemoSITfiRows(real: CanopusStaffMember[] | null): DemoSITfiRow[] {
  if (!real || real.length === 0) return [];
  const demoStaffOnly = real.filter(
    (m) =>
      (m.email ?? '').toLowerCase().endsWith(DEMO_STAFF_EMAIL_DOMAIN) && hasMultiWordName(m.name)
  );
  const ordered = demoStaffOnly.slice().sort((a, b) => a.userId.localeCompare(b.userId));
  const onDutyTarget = Math.min(6, ordered.length);
  const onDuty = ordered.slice(0, onDutyTarget);

  return onDuty.map((m, idx) => {
    const tfi = 12 + ((idx * 17) % 84);
    const layers: [number, number, number] = [
      clamp01_100(tfi - 5),
      clamp01_100(tfi),
      clamp01_100(tfi + 6),
    ];
    // Mirror the SI overlay numbers from applyDemoStaffOverlay.
    const siToday = 8 + ((idx * 3) % 11);
    const siOffCase = DEMO_SI_OFFCASE_BY_SLOT[idx % DEMO_SI_OFFCASE_BY_SLOT.length] ?? 5;
    let expected: number | null = null;
    let remainingForBenchmark: number | null = null;
    if (tfi >= 40) {
      expected = Math.max(1, Math.round(30 - tfi * 0.2));
    } else {
      remainingForBenchmark = 5 + (idx % 7);
    }
    return {
      name: m.name ?? m.userId,
      tfi,
      layers,
      siToday,
      siOffCase,
      expected,
      remainingForBenchmark,
    };
  });
}

// Overlay a synthetic `avgTfi` value onto each monthly
// trend entry so the Trends tab "Avg Team TFI" MiniChart can render an
// adoption-style upward curve. The backend `CanopusTrendsMonthlyEntry`
// type does not include this field — it's purely a client-side
// demo-mode addition.
//
// Values trend from ~58 to the upper 70s/low 80s across the months. We
// add a deterministic "+2 every step plus a small (idx % 3) wiggle" so
// the curve isn't a perfectly straight line but is fully reproducible
// across renders.
export function getDemoMonthlyTfiOverlay<T extends { monthLabel: string }>(
  monthly: T[]
): Array<T & { avgTfi: number }> {
  return monthly.map((m, idx) => ({
    ...m,
    avgTfi: Math.min(100, 58 + idx * 2 + (idx % 3)),
  }));
}

// Deterministic synthetic "Avg Team TFI" value for a
// leaderboard entry. Used by the Performance tab when demo mode is on
// to populate the "Avg Team TFI" metric (no real TFI data exists at the
// per-entry level yet). The hash is a simple sum of charCodes so the
// same entry name always produces the same value across renders.
export function getDemoTfiForLeaderboardEntry(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash += name.charCodeAt(i);
  }
  // Map into the 45..95 range (inclusive of the lower bound, capped at 94).
  return 45 + ((hash * 7) % 50);
}

// ─── Performance leaderboard SI overlay ───────────────────────────

// Overlay siTotal / siOffCase / siPerCase onto each
// leaderboard entry without touching the rest. The server returns real
// surgeon/staff/room/specialty/procedure names and real case counts but
// the SI collection is empty, so those three fields come back as zeros
// / nulls. In demo mode we replace them with deterministic plausible
// values derived from the entry's name and case count. siOffCase only
// applies to the staff bucket (the metric pill is staff-only); for
// every other bucket we keep it null so the UI doesn't pretend the
// number is meaningful.
function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h;
}

// Deterministic per-entry filler for every metric the
// performance leaderboard renders. Backend returns nulls for any metric
// without enough cases (avgDur / avgVariance / consistency / improvement
// / utilization / bestDay / trend), which leaves the demo with empty
// cells. We synthesize plausible values from the entry's name hash so
// the demo never shows '--'.
const DEMO_BEST_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function overlaySIOnEntry(
  entry: CanopusLeaderboardEntry,
  isStaff: boolean
): CanopusLeaderboardEntry {
  const cases = Math.max(0, entry.cases ?? 0);
  const h = hashName(entry.name);
  // 22..37 SI per case — keeps siPerCase in the same range the
  // Surgical Inquiries demo aggregates use (~27..32).
  const avgPerCase = 22 + (h % 16);
  const siTotal = cases * avgPerCase;
  // off-case SI is meaningful for every category in
  // demo mode (mirrors the SI byOR / bySpecialty / byProcedure rows the
  // SI tab fills). 6..17% of total, deterministic from the name hash.
  const offRatio = 0.06 + ((h >> 4) % 12) / 100;
  const siOffCase = Math.max(1, Math.round(siTotal * offRatio));
  const siPerCase = cases > 0 ? Math.round((siTotal / cases) * 10) / 10 : avgPerCase;

  // Duration-style metrics: synthesized from the same name hash so a
  // given entry always reports the same numbers across renders.
  const avgDurMin = entry.avgDur ?? 60 + (h % 91); // 60..150 min
  const avgVarMin = entry.avgVariance ?? Math.round((6 + ((h >> 2) % 12)) * 10) / 10; // 6..17.9
  const consistency = entry.consistency ?? 65 + ((h >> 3) % 31); // 65..95
  const improvement = entry.improvement ?? 5 + ((h >> 5) % 26); // 5..30
  const utilization = entry.utilization ?? 80 + ((h >> 6) % 21); // 80..100
  const bestDay = entry.bestDay ?? DEMO_BEST_DAYS[h % DEMO_BEST_DAYS.length] ?? 'Wed';

  // 6-month duration variance trend that gently improves. We synthesize a
  // value for *every* month so demo bar charts never show empty slots —
  // when the backend trend has a real value for a slot we keep it, but any
  // null cell is filled with the synthesized point for that slot.
  const realTrend = entry.trend ?? [];
  const trend = Array.from({ length: 6 }).map((_, i) => {
    const existing = realTrend[i];
    if (existing != null) return existing;
    const drift = ((h >> i) % 5) - 2;
    return Math.max(2, Math.round((avgVarMin - i * 1.2 + drift) * 10) / 10);
  });

  return {
    ...entry,
    siTotal,
    siOffCase,
    siPerCase,
    avgDur: avgDurMin,
    avgVariance: avgVarMin,
    consistency,
    improvement,
    utilization,
    bestDay,
    trend,
    // Bump cases up so consistency / improvement (which require ≥15
    // cases to mean anything) never render as "Needs more data".
    cases: isStaff ? Math.max(cases, 16) : Math.max(cases, 20),
  };
}

export function applyDemoSIToLeaderboard(
  real: CanopusPerformanceLeaderboard | null
): CanopusPerformanceLeaderboard | null {
  if (!real) return real;
  // Make sure trendMonths covers 6 slots — the variance-trend chart in the
  // detail panel maps month labels by index, so any missing label leaves
  // an empty cell under one of the bars.
  const now = new Date();
  const fallbackTrendMonths = Array.from({ length: 6 }).map((_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return d.toLocaleString('en-US', { month: 'short' });
  });
  const trendMonths: string[] = Array.from({ length: 6 }).map(
    (_, i) => real.trendMonths?.[i] ?? fallbackTrendMonths[i]!
  );
  return {
    ...real,
    trendMonths,
    surgeons: real.surgeons.map((e) => overlaySIOnEntry(e, false)),
    staff: real.staff.map((e) => overlaySIOnEntry(e, true)),
    rooms: real.rooms.map((e) => overlaySIOnEntry(e, false)),
    specialties: real.specialties.map((e) => overlaySIOnEntry(e, false)),
    procedures: real.procedures.map((e) => overlaySIOnEntry(e, false)),
  };
}

// ─── Demo performance leaderboard (base) ──────────────────────────

// Build a BASE leaderboard from the baked demo pools, shaped exactly like
// the real backend response: names + case counts are populated, but every
// SI / duration / variance / TFI field is left at the same zero/null the
// real backend returns when its SI collection is empty. The Performance tab
// then pipes this through the existing `applyDemoSIToLeaderboard` +
// `applyDemoSpecialtyNormalization` overlays, so all the SI/metric filling
// (and the demo-only "Avg Team TFI" pill) reuses one code path — no
// duplicated overlay logic here.
function baseLeaderboardEntry(
  name: string,
  specialty: string | null,
  position: string | null,
  cases: number
): CanopusLeaderboardEntry {
  return {
    name,
    specialty,
    position,
    cases,
    avgDur: null,
    avgVariance: null,
    consistency: null,
    improvement: null,
    utilization: null,
    siTotal: 0,
    siOffCase: null,
    siPerCase: null,
    bestDay: null,
    trend: [],
  };
}

export function getDemoPerformanceLeaderboard(
  period: CanopusSIPeriod,
  surgeons: InsightsSurgeon[] = DEMO_SURGEONS,
  staff: CanopusStaffMember[] = DEMO_STAFF.members
): CanopusPerformanceLeaderboard {
  // Scale case counts with the same period multiplier SI aggregates use so
  // switching 30d / 90d / YTD / All Time actually changes the leaderboard.
  const mult = PERIOD_MULTIPLIER[period] ?? 1;
  const scaleCases = (n: number) => Math.max(1, Math.round(n * mult));

  const surgeonEntries = surgeons
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((s, i) =>
      baseLeaderboardEntry(s.name, s.specialty ?? null, null, scaleCases(20 + ((i * 7) % 40)))
    );

  const staffEntries = staff
    .filter(
      (m) =>
        (m.email ?? '').toLowerCase().endsWith(DEMO_STAFF_EMAIL_DOMAIN) && hasMultiWordName(m.name)
    )
    .slice()
    .sort((a, b) => a.userId.localeCompare(b.userId))
    .map((m, i) =>
      baseLeaderboardEntry(m.name ?? m.userId, null, m.position, scaleCases(16 + ((i * 5) % 30)))
    );

  // Rooms — the 5 live demo rooms, in a stable order.
  const roomEntries = [...DEMO_ONGOING_ROOM_NAMES, ...DEMO_DONE_ROOM_NAMES].map((room, i) =>
    baseLeaderboardEntry(room, null, null, scaleCases(24 + ((i * 6) % 28)))
  );

  // Procedures + specialties — reuse the ONGOING/COMPLETED case templates so
  // the whole demo stays internally consistent.
  const caseTemplates = [
    ...ONGOING.map((o) => ({ procedure: o.procedureName, specialty: o.specialty })),
    ...COMPLETED.map((c) => ({ procedure: c.procedureName, specialty: c.specialty })),
  ];
  const procedureEntries = caseTemplates.map((t, i) =>
    baseLeaderboardEntry(t.procedure, t.specialty, null, scaleCases(14 + ((i * 4) % 22)))
  );

  const specialtyNames = Array.from(new Set(caseTemplates.map((t) => t.specialty)));
  const specialtyEntries = specialtyNames.map((name, i) =>
    baseLeaderboardEntry(name, null, null, scaleCases(40 + ((i * 11) % 60)))
  );

  return {
    period,
    generatedAtIso: new Date().toISOString(),
    surgeons: surgeonEntries,
    staff: staffEntries,
    rooms: roomEntries,
    specialties: specialtyEntries,
    procedures: procedureEntries,
    // Left empty; applyDemoSIToLeaderboard backfills 6 month labels.
    trendMonths: [],
  };
}

// ─── Demo specialty normalization ─────────────────────────────────

// Consolidate the variant specialty strings the demo
// backend surfaces into the cleaner labels we want on demo screens.
// Drop once the underlying data is cleaned up.
const DEMO_SPECIALTY_REMAP: Record<string, string> = {
  'neurological surgery': 'Neurosurgery',
  'neurosurgery & spine': 'Neurosurgery',
  cardiac: 'Cardiothoracic Surgery',
  'ortho spine': 'Orthopedic Surgery',
  'orthopedic spine surgery': 'Orthopedic Surgery',
};

export function normalizeDemoSpecialty(s: string | null | undefined): string | null {
  if (s == null) return null;
  const trimmed = s.trim();
  if (!trimmed) return null;
  return DEMO_SPECIALTY_REMAP[trimmed.toLowerCase()] ?? trimmed;
}

// Merge any leaderboard specialty bucket entries that
// collapse onto the same normalized name. Sums cases / SI counts,
// weighted-averages duration-style metrics, sums trend cells, and keeps
// the first non-null bestDay. Identifier fields stay null for the merged
// row since the merged group spans more than one real specialty.
function mergeLeaderboardSpecialties(
  entries: CanopusLeaderboardEntry[]
): CanopusLeaderboardEntry[] {
  const groups = new Map<string, CanopusLeaderboardEntry[]>();
  for (const e of entries) {
    const name = normalizeDemoSpecialty(e.name) ?? e.name;
    const renamed = { ...e, name };
    const arr = groups.get(name) ?? [];
    arr.push(renamed);
    groups.set(name, arr);
  }
  const merged: CanopusLeaderboardEntry[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      merged.push(group[0]!);
      continue;
    }
    const cases = group.reduce((s, e) => s + (e.cases ?? 0), 0);
    const weighted = (
      key: 'avgDur' | 'avgVariance' | 'consistency' | 'improvement' | 'utilization'
    ): number | null => {
      let num = 0;
      let den = 0;
      for (const e of group) {
        const v = e[key];
        const c = e.cases ?? 0;
        if (v != null && c > 0) {
          num += v * c;
          den += c;
        }
      }
      return den > 0 ? Math.round((num / den) * 10) / 10 : null;
    };
    const siTotal = group.reduce((s, e) => s + (e.siTotal ?? 0), 0);
    const offCases = group.filter((e) => e.siOffCase != null);
    const siOffCase =
      offCases.length > 0 ? offCases.reduce((s, e) => s + (e.siOffCase ?? 0), 0) : null;
    const siPerCase = cases > 0 && siTotal > 0 ? Math.round((siTotal / cases) * 10) / 10 : null;
    const trendLen = Math.max(...group.map((e) => e.trend.length));
    const trend: (number | null)[] = [];
    for (let i = 0; i < trendLen; i++) {
      let num = 0;
      let den = 0;
      for (const e of group) {
        const v = e.trend[i];
        if (v != null) {
          num += v;
          den += 1;
        }
      }
      trend.push(den > 0 ? Math.round((num / den) * 10) / 10 : null);
    }
    merged.push({
      name: group[0]!.name,
      specialty: null,
      position: null,
      cases,
      avgDur: weighted('avgDur'),
      avgVariance: weighted('avgVariance'),
      consistency: weighted('consistency'),
      improvement: weighted('improvement'),
      utilization: weighted('utilization'),
      siTotal,
      siOffCase,
      siPerCase,
      bestDay: group.find((e) => e.bestDay != null)?.bestDay ?? null,
      trend,
    });
  }
  return merged.sort((a, b) => (b.cases ?? 0) - (a.cases ?? 0));
}

// Apply specialty normalization across every leaderboard
// bucket. Surgeons / procedures rows get their `specialty` field remapped;
// the specialties bucket itself gets its `name` remapped and any
// duplicates merged.
export function applyDemoSpecialtyNormalization(
  real: CanopusPerformanceLeaderboard | null
): CanopusPerformanceLeaderboard | null {
  if (!real) return real;
  const renameOnEntry = (e: CanopusLeaderboardEntry): CanopusLeaderboardEntry => ({
    ...e,
    specialty: normalizeDemoSpecialty(e.specialty),
  });
  return {
    ...real,
    surgeons: real.surgeons.map(renameOnEntry),
    procedures: real.procedures.map(renameOnEntry),
    specialties: mergeLeaderboardSpecialties(real.specialties),
  };
}

// Apply specialty normalization across every trends
// payload. The specialtyVariance bucket gets its `specialty` remapped
// and any duplicates merged (sum cases, weighted-avg the variance
// metrics).
export function applyDemoSpecialtyToTrends(
  real: CanopusTrendsResult | null
): CanopusTrendsResult | null {
  if (!real) return real;
  const groups = new Map<string, CanopusTrendsSpecialtyVariance[]>();
  for (const v of real.specialtyVariance) {
    const name = normalizeDemoSpecialty(v.specialty) ?? v.specialty;
    const arr = groups.get(name) ?? [];
    arr.push({ ...v, specialty: name });
    groups.set(name, arr);
  }
  const specialtyVariance: CanopusTrendsSpecialtyVariance[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      specialtyVariance.push(group[0]!);
      continue;
    }
    const cases = group.reduce((s, e) => s + (e.cases ?? 0), 0);
    const weighted = (key: 'baselineVarMin' | 'currentVarMin' | 'reductionPct'): number | null => {
      let num = 0;
      let den = 0;
      for (const e of group) {
        const v = e[key];
        const c = e.cases ?? 0;
        if (v != null && c > 0) {
          num += v * c;
          den += c;
        }
      }
      return den > 0 ? Math.round((num / den) * 10) / 10 : null;
    };
    specialtyVariance.push({
      specialty: group[0]!.specialty,
      baselineVarMin: weighted('baselineVarMin'),
      currentVarMin: weighted('currentVarMin'),
      reductionPct: weighted('reductionPct'),
      cases,
    });
  }
  specialtyVariance.sort((a, b) => (b.cases ?? 0) - (a.cases ?? 0));
  return { ...real, specialtyVariance };
}

// Apply specialty normalization to each historical case
// row so the Historical tab's specialty column and filter dropdown both
// surface the cleaned labels.
export function applyDemoSpecialtyToHistoricalCases(
  cases: CanopusHistoricalCase[]
): CanopusHistoricalCase[] {
  return cases.map((c) => ({ ...c, specialty: normalizeDemoSpecialty(c.specialty) }));
}

// ─── Demo historical cases ───────────────────────────────────────

// Number of synthesized historical cases per period.
// Loose targets — the goal is enough rows that the table feels populated
// across pagination without dragging out the demo load.
const DEMO_HISTORICAL_COUNT_BY_PERIOD: Record<CanopusSIPeriod, number> = {
  today: 6,
  '7d': 22,
  '30d': 60,
  '90d': 150,
  ytd: 250,
  all: 400,
};

const DEMO_HISTORICAL_PERIOD_DAYS: Record<CanopusSIPeriod, number> = {
  today: 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
  ytd: 220,
  all: 365,
};

// All 17 ORs in numeric order — historical cases are spread across every
// room rather than just the live demo ones so the OR filter feels real.
const DEMO_HISTORICAL_OR_NAMES = Array.from({ length: 17 }, (_, i) => `OR ${i + 1}`);

// Build a completed-case version of a procedure
// template. All steps are marked done with click timestamps spread
// across the case window; every phase has a concrete durationSec (no
// inProgress phase).
function buildCompletedStepsAndPhases(
  template: OngoingCaseDescriptor,
  startMs: number,
  durationSec: number,
  driftSeed: number
): { steps: CanopusStep[]; phases: CanopusPhaseTiming[] } {
  const endMs = startMs + durationSec * 1000;
  const span = Math.max(1, endMs - startMs);
  const tick = span / Math.max(1, template.steps.length + 1);
  const steps: CanopusStep[] = template.steps.map((spec, i) => ({
    name: spec.name,
    phase: PHASES[spec.phase] ?? 'Unknown',
    phaseIndex: spec.phase,
    source: 1,
    done: true,
    clickTimeIso: new Date(startMs + tick * (i + 1)).toISOString(),
  }));
  // Apply a small deterministic drift per phase so each historical row
  // doesn't read as a carbon copy of the template avg.
  const phases: CanopusPhaseTiming[] = template.phaseDurations.map((d, i) => {
    const baseAvg = d.avgSec ?? 600;
    const drift = (((driftSeed + i * 7) % 21) - 10) / 100; // -10%..+10%
    return {
      name: PHASES[i] ?? `Phase ${i}`,
      durationSec: Math.max(60, Math.round(baseAvg * (1 + drift))),
      avgSec: d.avgSec,
      inProgress: false,
    };
  });
  return { steps, phases };
}

// Rotation used to fill the demo "Device" tile on the
// historical case detail panel. Mirrors the device distribution we apply
// on the live OR Overview (mostly iPad, occasional Vision Pro).
const DEMO_HISTORICAL_DEVICES = ['iPad', 'iPad', 'iPad', 'Apple Vision Pro'];

// Synthesize a list of completed historical cases.
// Cycles through the three ONGOING procedure templates (they have full
// step + phase specs), spreads them deterministically across the
// requested period, and assigns each row to a real surgeon by specialty
// when one is available. Falls back to a generic "Dr. Demo N" name when
// the real surgeon list is empty / null.
export function getDemoHistoricalCases(
  period: CanopusSIPeriod,
  realSurgeons: InsightsSurgeon[] | null,
  realStaff: CanopusStaffMember[] | null = null
): CanopusHistoricalCase[] {
  const count = DEMO_HISTORICAL_COUNT_BY_PERIOD[period];
  const days = DEMO_HISTORICAL_PERIOD_DAYS[period];
  const now = Date.now();
  const periodMs = days * 24 * 60 * 60 * 1000;
  // Index real surgeons by normalized specialty so each procedure template
  // can pick a matching surgeon. Falls back to round-robin from the full
  // list when the specialty bucket is empty.
  const surgeonsBySpecialty = new Map<string, InsightsSurgeon[]>();
  const surgeonPool: InsightsSurgeon[] = realSurgeons ?? [];
  for (const s of surgeonPool) {
    const key = (s.specialty ?? '').toLowerCase();
    const arr = surgeonsBySpecialty.get(key) ?? [];
    arr.push(s);
    surgeonsBySpecialty.set(key, arr);
  }
  const pickSurgeon = (specialty: string, idx: number): { id: string; name: string } => {
    const matches = surgeonsBySpecialty.get(specialty.toLowerCase());
    if (matches && matches.length > 0) {
      const s = matches[idx % matches.length]!;
      return { id: s.id, name: s.name };
    }
    if (surgeonPool.length > 0) {
      const s = surgeonPool[idx % surgeonPool.length]!;
      return { id: s.id, name: s.name };
    }
    return { id: `demo-surgeon-${idx}`, name: `Dr. Demo ${idx + 1}` };
  };

  // Demo staff pool for filling each case's Scrub Tech /
  // Circ Nurse — same demo-domain, multi-word, userId-sorted slice the
  // Staff tab uses, so the same set of real names cycles through both
  // views. Falls back to placeholder names when no real staff fetch is
  // available.
  const staffPool: CanopusStaffMember[] = realStaff
    ? realStaff
        .filter(
          (m) =>
            (m.email ?? '').toLowerCase().endsWith(DEMO_STAFF_EMAIL_DOMAIN) &&
            hasMultiWordName(m.name)
        )
        .slice()
        .sort((a, b) => a.userId.localeCompare(b.userId))
    : [];
  const pickStaffPair = (idx: number): { scrub: string; circ: string } => {
    if (staffPool.length >= 2) {
      const scrub = staffPool[idx % staffPool.length]!.name ?? `Scrub Tech ${idx + 1}`;
      // Offset by 1 so the same person never fills both roles on a
      // single case.
      const circ =
        staffPool[(idx + 1 + (idx % 3)) % staffPool.length]!.name ?? `Circ Nurse ${idx + 1}`;
      return { scrub, circ };
    }
    return {
      scrub: staffPool[0]?.name ?? `Scrub Tech ${idx + 1}`,
      circ: `Circ Nurse ${(idx % 5) + 1}`,
    };
  };

  const out: CanopusHistoricalCase[] = [];
  for (let i = 0; i < count; i++) {
    const template = ONGOING[i % ONGOING.length]!;
    const surgeon = pickSurgeon(template.specialty, i);
    // Spread starts evenly across the period; older rows toward the
    // start, newer rows toward `now`.
    const ageMs = Math.round((periodMs * (i + 1)) / (count + 1));
    const startMs = now - ageMs;
    // Per-case duration drifts ±15% around the template avg, deterministic
    // by case index so re-renders stay stable.
    const drift = (((i * 13) % 31) - 15) / 100;
    const durationSec = Math.max(600, Math.round(template.avgDurationSec * (1 + drift)));
    const { steps, phases } = buildCompletedStepsAndPhases(template, startMs, durationSec, i);
    const intraStart = new Date(startMs + Math.round(durationSec * 0.18) * 1000).toISOString();
    const intraEnd = new Date(startMs + Math.round(durationSec * 0.82) * 1000).toISOString();
    const last = surgeon.name.split(/\s+/).slice(-1)[0] ?? surgeon.name;
    const staff = pickStaffPair(i);
    out.push({
      procedureId: `demo-hist-${i + 1}`,
      dateIso: new Date(startMs).toISOString(),
      surgeonId: surgeon.id,
      surgeonName: surgeon.name,
      specialty: normalizeDemoSpecialty(template.specialty),
      procedureName: template.procedureName,
      roomName: DEMO_HISTORICAL_OR_NAMES[i % DEMO_HISTORICAL_OR_NAMES.length]!,
      totalTimeSec: durationSec,
      avgDurationSec: template.avgDurationSec,
      caseCount: template.caseCount + (i % 7),
      profileName: `${last} — ${template.profileTail}`,
      startTimeIso: new Date(startMs).toISOString(),
      intraoperativeStartTimeIso: intraStart,
      intraoperativeEndTimeIso: intraEnd,
      phases,
      steps,
      scrubTechName: staff.scrub,
      circNurseName: staff.circ,
      deviceName: DEMO_HISTORICAL_DEVICES[i % DEMO_HISTORICAL_DEVICES.length]!,
    });
  }
  // Newest first to match the real backend's sort order.
  out.sort((a, b) => b.startTimeIso.localeCompare(a.startTimeIso));
  return out;
}

// ─── Trends monthly SI overlay ────────────────────────────────────

// Overlay siTotal / siOffCase / siPerCase plus the
// duration / variance / longRate fields onto each monthly trend entry.
// Mirrors `applyDemoSIToLeaderboard` but for the Trends tab's per-month
// rows, where the same backend gap (empty SI collection, sparse case
// baselines) leaves the MiniCharts mostly empty.
//
// Curves are gentle so the charts look like an improving facility:
//   • siTotal:        ~2.0k → ~3.6k       (growth)
//   • avgDurMin:      ~84   → ~80         (slight tightening)
//   • avgVarianceMin: ~14   → ~7          (improvement)
//   • longRatePct:    ~22%  → ~10%        ("Cases Above Variance" drops)
export function getDemoMonthlySIOverlay(
  monthly: CanopusTrendsMonthlyEntry[]
): CanopusTrendsMonthlyEntry[] {
  const last = Math.max(1, monthly.length - 1);
  return monthly.map((m, idx) => {
    const total = 2000 + idx * 320;
    const offCase = Math.round(total * 0.17);
    const cases = Math.max(1, m.cases);
    const perCase = Math.round((total / cases) * 10) / 10;
    // Linear interpolation between the start/end anchors so any number
    // of months renders a clean trend.
    const t = idx / last;
    const avgDurMin = m.avgDurMin != null ? m.avgDurMin : Math.round((84 - 4 * t) * 10) / 10;
    const avgVarianceMin =
      m.avgVarianceMin != null ? m.avgVarianceMin : Math.round((14 - 7 * t) * 10) / 10;
    const longRatePct = m.longRatePct != null ? m.longRatePct : Math.round((22 - 12 * t) * 10) / 10;
    // Companion below-variance curve — short rate eases up over time as
    // teams hit baselines more consistently. Runtime over/under averages
    // tighten in lockstep.
    const shortRatePct =
      m.shortRatePct != null ? m.shortRatePct : Math.round((14 - 6 * t) * 10) / 10;
    const runtimeAboveMin =
      m.runtimeAboveMin != null ? m.runtimeAboveMin : Math.round((18 - 6 * t) * 10) / 10;
    const runtimeBelowMin =
      m.runtimeBelowMin != null ? m.runtimeBelowMin : Math.round((11 - 3 * t) * 10) / 10;
    return {
      ...m,
      siTotal: total,
      siOffCase: offCase,
      siPerCase: perCase,
      avgDurMin,
      avgVarianceMin,
      longRatePct,
      shortRatePct,
      runtimeAboveMin,
      runtimeBelowMin,
    };
  });
}

// ─── Demo trends (fully synthesized) ─────────────────────────────

// How many monthly rows to synthesize per period. Drives
// the MiniChart x-axis length so 7 days renders a short timeline while
// 90 days / YTD render fuller ones.
function demoTrendsMonthsForPeriod(period: CanopusSIPeriod, now: Date): number {
  if (period === 'today' || period === '7d') return 2;
  if (period === '30d') return 2;
  if (period === '90d') return 3;
  if (period === 'ytd') return Math.max(1, now.getMonth() + 1);
  return 12; // 'all'
}

// How much to scale the case-count / sample-size fields
// by period. Picks for 7d are tiny so a 7-day window doesn't read as a
// quarter-sized facility.
const DEMO_TRENDS_PERIOD_SCALE: Record<CanopusSIPeriod, number> = {
  today: 0.04,
  '7d': 0.18,
  '30d': 0.5,
  '90d': 1,
  ytd: 1.7,
  all: 2.6,
};

// How much variance has been "improved" by the end of
// the window. Short windows (7d) capture less progress; long ones (YTD /
// all) show a deeper reduction.
const DEMO_TRENDS_VARIANCE_DELTA: Record<CanopusSIPeriod, number> = {
  today: 1,
  '7d': 2,
  '30d': 3.5,
  '90d': 7,
  ytd: 9,
  all: 11,
};

// Fixed pool of weekday + room labels for the duration
// variance heatmap. Every cell gets a value (never null) so the demo
// chart has no holes.
const DEMO_TRENDS_HEATMAP_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DEMO_TRENDS_HEATMAP_ROOMS = ['OR 1', 'OR 3', 'OR 5', 'OR 7', 'OR 12'];

// Adoption-curve buckets — case-count ranges with their
// average duration-variance values. Variance drops as case count grows.
const DEMO_TRENDS_ADOPTION_BUCKETS: Array<{ label: string; varMin: number; cases: number }> = [
  { label: '1–5 cases', varMin: 18, cases: 38 },
  { label: '6–10 cases', varMin: 14, cases: 52 },
  { label: '11–15 cases', varMin: 11, cases: 64 },
  { label: '16–25 cases', varMin: 8, cases: 92 },
  { label: '26+ cases', varMin: 5, cases: 140 },
];

// Static procedure pool used to fan out
// surgeonProcedureVariance + surgeonDurationIntel rows. Pulls procedure
// names + specialties from the existing ONGOING / COMPLETED templates so
// the rest of the demo stays consistent.
const DEMO_TRENDS_PROCEDURES: Array<{ name: string; specialty: string; longestPhase: string }> = [
  { name: 'Lumbar Microdiscectomy', specialty: 'Spine', longestPhase: 'Primary Procedure' },
  { name: 'ALIF L4-L5', specialty: 'Spine', longestPhase: 'Primary Procedure' },
  { name: 'Craniotomy', specialty: 'Neurosurgery', longestPhase: 'Primary Procedure' },
  { name: 'Rotator Cuff Repair', specialty: 'Orthopedic Surgery', longestPhase: 'Exposure' },
  { name: 'Cervical Laminoplasty', specialty: 'Spine', longestPhase: 'Primary Procedure' },
];

function pickDemoSurgeons(
  realSurgeons: InsightsSurgeon[] | null,
  count: number
): Array<{ id: string; name: string; specialty: string | null }> {
  const pool = (realSurgeons ?? []).slice();
  // Deterministic order so re-renders don't shuffle rows.
  pool.sort((a, b) => a.id.localeCompare(b.id));
  const out: Array<{ id: string; name: string; specialty: string | null }> = [];
  for (let i = 0; i < count; i++) {
    if (pool.length > 0) {
      const s = pool[i % pool.length]!;
      out.push({ id: s.id, name: s.name, specialty: s.specialty ?? null });
    } else {
      out.push({ id: `demo-surgeon-${i}`, name: `Dr. Demo ${i + 1}`, specialty: null });
    }
  }
  return out;
}

// Fully synthesized trends payload. Real fetch is
// bypassed entirely so demo screens never show empty MiniCharts or
// heatmap cells. The only "real" thing we keep is surgeon names, sourced
// from `realSurgeons` when available (falls back to "Dr. Demo N").
export function getDemoTrends(
  period: CanopusSIPeriod,
  realSurgeons: InsightsSurgeon[] | null
): CanopusTrendsResult {
  const now = new Date();
  const monthsBack = demoTrendsMonthsForPeriod(period, now);
  const scale = DEMO_TRENDS_PERIOD_SCALE[period];
  const varianceDelta = DEMO_TRENDS_VARIANCE_DELTA[period];
  const sc = (n: number) => Math.max(1, Math.round(n * scale));

  // ── monthlyTrends ─────────────────────────────────────────────
  // Window-shaped: 7d renders 7 *daily* buckets so the MiniCharts read
  // as a week, not a calendar year. Longer periods stay on monthly
  // buckets. Case counts and SI totals scale down for tighter windows
  // so the chart reflects the selected timeframe rather than a fixed
  // 6-month curve.
  const monthlyTrends: CanopusTrendsMonthlyEntry[] = [];
  if (period === '7d' || period === 'today') {
    const dayCount = 7;
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (let i = 0; i < dayCount; i++) {
      const offset = dayCount - 1 - i;
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
      const monthLabel = dayLabels[d.getDay()]!;
      const monthIso = d.toISOString().slice(0, 10);
      const t = i / (dayCount - 1);
      // 6..14 cases per day for a demo facility on a 7-day window.
      const cases = Math.max(1, Math.round(6 + ((i * 3) % 9)));
      const siTotal = Math.round((80 + i * 10) * 1.4); // ~110..210
      const siOffCase = Math.round(siTotal * 0.17);
      // Cases-above/below and runtimes-above/below are genuinely
      // independent signals in real data (you can be over often but only
      // slightly, or under rarely but by a lot). The demo curves below
      // intentionally use different shapes — not inverse trends — so the
      // four charts don't read as mirrors.
      const swingLong = Math.sin(i * 0.9) * 4; // smooth wave around the trend
      const swingShort = Math.cos(i * 1.3 + 1) * 3; // out-of-phase wave
      const noiseAbove = ((i * 3 + 1) % 7) - 3;
      const noiseBelow = ((i * 11) % 5) - 2;
      monthlyTrends.push({
        monthIso,
        monthLabel,
        cases,
        avgDurMin: Math.round((84 - varianceDelta * 0.5 * t) * 10) / 10,
        avgVarianceMin: Math.round((14 - varianceDelta * t) * 10) / 10,
        // longRate: gentle downward trend, mid-window peaks.
        longRatePct: Math.max(1, Math.round((20 - varianceDelta * 1.0 * t + swingLong) * 10) / 10),
        // shortRate: rough mid-teens with its own wave; not the inverse
        // of longRate.
        shortRatePct: Math.max(
          1,
          Math.round((13 - varianceDelta * 0.2 * t + swingShort) * 10) / 10
        ),
        // runtimeAbove: declining but with deeper bumps — magnitude of
        // each overrun shrinks faster than the rate of overruns.
        runtimeAboveMin: Math.max(
          1,
          Math.round((22 - varianceDelta * 1.3 * t + noiseAbove) * 10) / 10
        ),
        // runtimeBelow: roughly flat — when teams beat baseline they
        // tend to save a consistent amount of time.
        runtimeBelowMin: Math.max(
          1,
          Math.round((8 + varianceDelta * 0.1 * t + noiseBelow) * 10) / 10
        ),
        siTotal,
        siOffCase,
        siPerCase: Math.round((siTotal / cases) * 10) / 10,
      });
    }
  } else {
    for (let i = 0; i < monthsBack; i++) {
      const monthOffset = monthsBack - 1 - i;
      const d = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
      const monthLabel = d.toLocaleString('en-US', { month: 'short' });
      const monthIso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const t = monthsBack > 1 ? i / (monthsBack - 1) : 1;
      // Total cases per month around 90, scaled by the period's pace.
      const cases = Math.max(1, Math.round((70 + i * 8) * Math.min(1.4, scale * 1.2)));
      const siTotal = Math.round((2000 + i * 320) * Math.min(1.4, scale * 1.2));
      const siOffCase = Math.round(siTotal * 0.17);
      // Cases-above/below and runtimes-above/below are independent signals
      // in real data — not inverses. Each demo curve uses a distinct shape
      // so the four charts aren't visual mirrors after the per-chart
      // auto-scale.
      const swingLong = Math.sin(i * 0.9) * 4;
      const swingShort = Math.cos(i * 1.3 + 1) * 3;
      const noiseAbove = ((i * 3 + 1) % 7) - 3;
      const noiseBelow = ((i * 11) % 5) - 2;
      monthlyTrends.push({
        monthIso,
        monthLabel,
        cases,
        // Avg / variance drift more dramatically over longer windows so
        // the curves don't look identical between periods.
        avgDurMin: Math.round((84 - varianceDelta * 0.5 * t) * 10) / 10,
        avgVarianceMin: Math.round((14 - varianceDelta * t) * 10) / 10,
        longRatePct: Math.max(1, Math.round((20 - varianceDelta * 1.0 * t + swingLong) * 10) / 10),
        shortRatePct: Math.max(
          1,
          Math.round((13 - varianceDelta * 0.2 * t + swingShort) * 10) / 10
        ),
        runtimeAboveMin: Math.max(
          1,
          Math.round((22 - varianceDelta * 1.3 * t + noiseAbove) * 10) / 10
        ),
        runtimeBelowMin: Math.max(
          1,
          Math.round((8 + varianceDelta * 0.1 * t + noiseBelow) * 10) / 10
        ),
        siTotal,
        siOffCase,
        siPerCase: Math.round((siTotal / cases) * 10) / 10,
      });
    }
  }

  // ── specialtyVariance ─────────────────────────────────────────
  // Baseline stays fixed (it's historical), but current variance moves
  // closer to baseline for shorter periods (less progress) and lower
  // for longer ones (more accumulated reduction). Case counts scale
  // with the period window.
  const specialtyVarianceBase: Array<{ specialty: string; baseline: number; cases: number }> = [
    { specialty: 'Spine', baseline: 16.2, cases: 142 },
    { specialty: 'Neurosurgery', baseline: 19.8, cases: 78 },
    { specialty: 'Orthopedic Surgery', baseline: 12.4, cases: 96 },
    { specialty: 'Cardiothoracic Surgery', baseline: 18.1, cases: 54 },
  ];
  const specialtyVariance: CanopusTrendsSpecialtyVariance[] = specialtyVarianceBase.map((b) => {
    const currentVarMin = Math.max(2, Math.round((b.baseline - varianceDelta) * 10) / 10);
    const reductionPct = Math.max(0, Math.round(((b.baseline - currentVarMin) / b.baseline) * 100));
    return {
      specialty: b.specialty,
      baselineVarMin: b.baseline,
      currentVarMin,
      reductionPct,
      cases: sc(b.cases),
    };
  });

  // ── surgeonProcedureVariance ─────────────────────────────────
  // One row per (demo surgeon × first 3 procedure templates). Mixes
  // trend categories so the timeline view shows all three states. Both
  // baseline and total case counts scale with period.
  const sxpSurgeons = pickDemoSurgeons(realSurgeons, 6);
  const surgeonProcedureVariance: CanopusTrendsSurgeonProcedureVariance[] = [];
  sxpSurgeons.forEach((s, si) => {
    DEMO_TRENDS_PROCEDURES.slice(0, 3).forEach((p, pi) => {
      const slot = si * 3 + pi;
      // Cycle through 'improving' / 'stable' / 'building' deterministically.
      const trendBucket: CanopusTrendsSurgeonProcedureVariance['trend'] =
        slot % 7 === 6 ? 'building' : slot % 3 === 0 ? 'improving' : 'stable';
      const baselineVarMin = 12 + (slot % 8); // 12..19
      const currentVarMin =
        trendBucket === 'building'
          ? baselineVarMin
          : trendBucket === 'improving'
            ? Math.max(3, baselineVarMin - Math.round(varianceDelta * 0.8) - (slot % 4))
            : Math.max(5, baselineVarMin - Math.round(varianceDelta * 0.2));
      const reductionPct =
        trendBucket === 'building'
          ? null
          : Math.max(0, Math.round(((baselineVarMin - currentVarMin) / baselineVarMin) * 100));
      const baselineCases = sc(trendBucket === 'building' ? 6 + (slot % 4) : 15 + (slot % 6));
      surgeonProcedureVariance.push({
        surgeonName: s.name,
        procedureName: p.name,
        baselineVarMin,
        currentVarMin,
        reductionPct,
        baselineCases,
        totalCases: baselineCases + sc(slot % 12),
        trend: trendBucket,
      });
    });
  });

  // ── varianceHeatmap ───────────────────────────────────────────
  // Every cell populated; values scale with the period so 7-day windows
  // run a touch tighter than YTD ones (less time for outliers to land).
  const heatmapBaseMax = 17 - Math.min(6, Math.round(varianceDelta * 0.4));
  const heatmapBaseMin = Math.max(3, 5 - Math.min(2, Math.round(varianceDelta * 0.1)));
  const heatmapRange = Math.max(2, heatmapBaseMax - heatmapBaseMin);
  const varianceHeatmap: CanopusTrendsVarianceHeatmap = {
    rooms: DEMO_TRENDS_HEATMAP_ROOMS.slice(),
    days: DEMO_TRENDS_HEATMAP_DAYS.slice(),
    matrix: DEMO_TRENDS_HEATMAP_DAYS.map((_, dayIdx) =>
      DEMO_TRENDS_HEATMAP_ROOMS.map((__, roomIdx) => {
        const v = heatmapBaseMin + ((dayIdx * 3 + roomIdx * 5) % heatmapRange);
        return Math.round(v * 10) / 10;
      })
    ),
  };

  // ── adoptionCurve ─────────────────────────────────────────────
  // Variance values drop slightly for longer periods (more cases per
  // bucket = more practiced = tighter variance). Case counts scale too.
  const adoptionCurve: CanopusTrendsAdoptionBucket[] = DEMO_TRENDS_ADOPTION_BUCKETS.map((b) => ({
    bucket: b.label,
    avgVarianceMin: Math.max(2, Math.round((b.varMin - varianceDelta * 0.25) * 10) / 10),
    cases: sc(b.cases),
  }));

  // ── reliableAvgsPct ───────────────────────────────────────────
  // Reliable averages need ≥15 cases — short windows have fewer
  // qualifying (surgeon, procedure) pairs.
  const reliableAvgsPct = Math.min(94, Math.max(22, Math.round(40 + varianceDelta * 4.5)));

  // ── surgeonDurationIntel ─────────────────────────────────────
  const intelSurgeons = pickDemoSurgeons(realSurgeons, 6);
  const surgeonDurationIntel: CanopusTrendsSurgeonDurationIntel[] = intelSurgeons.map((s, idx) => {
    const proc = DEMO_TRENDS_PROCEDURES[idx % DEMO_TRENDS_PROCEDURES.length]!;
    return {
      surgeonName: s.name,
      topProcedure: proc.name,
      cases: sc(24 + ((idx * 7) % 30)), // base 24..53, scaled by period
      avgDurMin: 68 + ((idx * 9) % 60),
      longestPhaseName: proc.longestPhase,
      longestPhaseAvgMin: 28 + ((idx * 5) % 24),
      // Running-long % is lower in shorter windows (less time to drift).
      runningLongPct: Math.max(
        1,
        Math.min(35, 8 + ((idx * 4) % 20) - Math.round(varianceDelta * 0.3))
      ),
      avgPreOpSetupMin: 12 + ((idx * 3) % 8),
    };
  });

  // ── roomGapTimes ─────────────────────────────────────────────
  const roomGapTimes: CanopusTrendsRoomGap[] = DEMO_TRENDS_HEATMAP_ROOMS.map((roomName, idx) => ({
    roomName,
    lastGapMin: 14 + ((idx * 6) % 22),
    avgGapMin: 18 + ((idx * 4) % 14),
    gapsMeasured: sc(22 + ((idx * 9) % 18)),
  }));

  // ── kpis ──────────────────────────────────────────────────────
  const totalRecordedCases = monthlyTrends.reduce((sum, m) => sum + (m.cases ?? 0), 0);
  const recentAvgDur =
    monthlyTrends.length > 0 ? monthlyTrends[monthlyTrends.length - 1]!.avgDurMin : null;
  const kpis = {
    totalRecordedCases,
    avgDurationMin: recentAvgDur,
    profilesCount: sc(28),
    surgeonsCount: Math.max(8, intelSurgeons.length),
  };

  return {
    period,
    generatedAtIso: now.toISOString(),
    monthlyTrends,
    specialtyVariance,
    surgeonProcedureVariance,
    varianceHeatmap,
    adoptionCurve,
    reliableAvgsPct,
    surgeonDurationIntel,
    roomGapTimes,
    kpis,
  };
}
